import type { RunAggregateProjectionV2, WebTestRun } from '@blume/web-test-model'
import { ExternalLink } from 'lucide-react'
import { useMemo } from 'react'

import { shortSha } from '@/design/format'
import { toneText } from '@/design/status'
import { cn } from '@/lib/utils'
import { fallbackSurface, PullRequestAuthorAvatar } from '@/ui/atoms/avatar-tile'

import {
  BackLink,
  EmptyState,
  GroupCard,
  KeyValueList,
  MetaLine,
  RunList,
  SectionLabel
} from './parts'
import { PullRequestGitHubFacts } from './pr-metadata'
import {
  mergeAnswer,
  pullRequestGroups,
  pullRequestHref,
  stalePullRequestKeys,
  type PullRequestGroup
} from './pr-shared'

/**
 * One pull-request revision: the merge verdict up top, every run belonging
 * to the revision, and honest handling of superseded revisions — enough to
 * answer "is this safe to merge?" on a single page.
 */
export function PullRequestDetailPage({
  runs,
  aggregates,
  groupKey,
  now
}: {
  runs: WebTestRun[]
  aggregates?: RunAggregateProjectionV2[]
  groupKey: string
  now: number
}) {
  const groups = useMemo(() => pullRequestGroups(runs, aggregates), [aggregates, runs])
  const stale = useMemo(() => stalePullRequestKeys(groups), [groups])
  const group = groups.find((candidate) => candidate.key === groupKey)

  if (!group) {
    return (
      <>
        <BackLink href="#/pull-requests" label="Pull requests" />
        <EmptyState>
          No verification is recorded for this pull request revision. Its runs may have been
          retained out, or the link is stale.
        </EmptyState>
      </>
    )
  }

  const progress = group.progress
  const answer = mergeAnswer(progress)
  const AnswerIcon = answer.icon
  const superseded = stale.has(group.key)
  const siblingRevisions = groups.filter(
    (candidate) =>
      candidate.key !== group.key &&
      candidate.repository === group.repository &&
      candidate.pullRequest === group.pullRequest
  )
  // stalePullRequestGroupKeys marks every revision after the newest, so the
  // latest revision of this PR is the sibling that is not stale.
  const latestRevision = superseded
    ? [group, ...siblingRevisions].find((candidate) => !stale.has(candidate.key))
    : undefined
  const githubUrl =
    group.repository && group.pullRequest
      ? `https://github.com/${group.repository}/pull/${group.pullRequest}`
      : undefined

  return (
    <>
      <BackLink href="#/pull-requests" label="Pull requests" />

      <header className="mb-8 max-w-3xl">
        <div className="flex items-start gap-3">
          <PullRequestAuthorAvatar
            author={group.metadata?.author}
            size="large"
            label={`Pull request ${group.pullRequest ?? ''}`}
          />
          <div className="min-w-0">
            <h1 className="text-2xl leading-7 font-semibold tracking-tight">
              {group.metadata?.title ?? 'Pull request'}
            </h1>
            {group.pullRequest ? (
              <p className="mt-1 text-sm leading-4 text-muted-foreground">
                PR{' '}
                <span
                  className="font-mono font-medium tabular-nums"
                  style={{
                    color: fallbackSurface(`pull-request:${group.pullRequest}`).backgroundColor
                  }}
                >
                  #{group.pullRequest}
                </span>
              </p>
            ) : null}
          </div>
        </div>
        <div className="mt-3">
          <MetaLine
            items={[
              group.repository,
              group.sourceSha ? (
                <span key="sha" className="font-mono">
                  commit {shortSha(group.sourceSha)}
                </span>
              ) : undefined
            ]}
          />
        </div>

        <section
          aria-label="Merge readiness"
          className="mt-4 rounded-lg border border-border/80 bg-card/45 px-4 py-3"
        >
          <p
            className={cn(
              'inline-flex items-center gap-2 text-base font-semibold',
              toneText[answer.tone]
            )}
          >
            <AnswerIcon
              aria-hidden
              className={cn('size-4.5', answer.tone === 'info' && 'animate-spin')}
            />
            {answer.label}
          </p>
          {group.metadata ? (
            <div className="mt-2 border-t border-border/70 pt-2">
              <PullRequestGitHubFacts metadata={group.metadata} />
            </div>
          ) : null}
        </section>

        {group.metadata?.description ? (
          <section aria-label="Pull request description" className="mt-6 max-w-prose">
            <SectionLabel>Description</SectionLabel>
            <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-muted-foreground">
              {group.metadata.description}
            </p>
          </section>
        ) : null}

        {superseded ? (
          <p className="mt-4 max-w-prose rounded-md border-l-2 border-status-warn bg-status-warn-surface/50 px-4 py-3 text-sm leading-6">
            <span className={cn('font-medium', toneText.warn)}>
              This revision has been superseded.
            </span>{' '}
            A newer push to {group.label} replaced it, so this verdict no longer describes the
            branch head.{' '}
            {latestRevision ? (
              <a
                href={pullRequestHref(latestRevision.key)}
                className="font-medium underline underline-offset-2 focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
              >
                Go to the latest revision
                {latestRevision.sourceSha ? ` (${shortSha(latestRevision.sourceSha)})` : ''}
              </a>
            ) : null}
          </p>
        ) : null}
      </header>

      <section aria-label="Runs in this revision">
        <GroupCard header={<SectionLabel>Runs in this revision ({progress.total})</SectionLabel>}>
          {group.runs.length > 0 ? (
            <RunList runs={group.runs} now={now} hideSource avatarSize="small" />
          ) : progress.primaryRunId ? (
            <p className="py-2 text-[13px] text-muted-foreground">
              Run details are outside the loaded page.{' '}
              <a
                href={`#/runs/${encodeURIComponent(progress.primaryRunId)}`}
                className="font-medium text-foreground underline underline-offset-4"
              >
                Open the decisive run
              </a>
            </p>
          ) : null}
        </GroupCard>
      </section>

      {siblingRevisions.length > 0 ? (
        <section aria-label="Other revisions of this pull request" className="mt-8">
          <GroupCard
            className="max-w-2xl"
            header={<SectionLabel>Other revisions of this pull request</SectionLabel>}
          >
            <ul className="space-y-1">
              {siblingRevisions.map((revision) => (
                <SiblingRevisionRow
                  key={revision.key}
                  revision={revision}
                  isLatest={!stale.has(revision.key)}
                />
              ))}
            </ul>
          </GroupCard>
        </section>
      ) : null}

      <section aria-label="Pull request reference" className="mt-12 max-w-2xl">
        <SectionLabel>Reference</SectionLabel>
        <div className="mt-2">
          <KeyValueList
            items={[
              group.repository ? { key: 'Repository', value: group.repository } : undefined,
              group.pullRequest
                ? { key: 'Pull request', value: `#${group.pullRequest}` }
                : undefined,
              group.metadata
                ? {
                    key: 'Checks on latest commit',
                    value: `${group.metadata.checks.passed}/${group.metadata.checks.total} passed`
                  }
                : undefined,
              group.metadata
                ? {
                    key: 'Review threads',
                    value: `${group.metadata.reviewThreads.unresolved}/${group.metadata.reviewThreads.total} unresolved`
                  }
                : undefined,
              group.sourceSha
                ? {
                    key: 'Revision',
                    value: <span className="font-mono">{shortSha(group.sourceSha)}</span>
                  }
                : undefined,
              githubUrl
                ? {
                    key: 'On GitHub',
                    value: (
                      <a
                        href={githubUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 font-medium text-primary underline-offset-2 hover:underline focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
                      >
                        {group.repository}#{group.pullRequest}
                        <ExternalLink aria-hidden className="size-3" />
                        <span className="sr-only"> (opens GitHub in a new tab)</span>
                      </a>
                    )
                  }
                : undefined
            ]}
          />
        </div>
      </section>
    </>
  )
}

function SiblingRevisionRow({
  revision,
  isLatest
}: {
  revision: PullRequestGroup
  isLatest: boolean
}) {
  const answer = mergeAnswer(revision.progress)
  const AnswerIcon = answer.icon
  return (
    <li>
      <a
        href={pullRequestHref(revision.key)}
        className="group -mx-3 flex flex-wrap items-baseline gap-x-3 gap-y-0.5 rounded-md px-3 py-2.5 transition-colors hover:bg-accent focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
      >
        <span className="font-mono text-[13px] font-semibold">
          {revision.sourceSha ? shortSha(revision.sourceSha) : revision.key}
        </span>
        <span className="text-[13px] text-muted-foreground">
          {isLatest ? 'Latest revision' : 'Older revision'} · {revision.progress.total} run
          {revision.progress.total === 1 ? '' : 's'}
        </span>
        <span
          className={cn(
            'inline-flex items-center gap-1.5 text-[13px] font-medium',
            toneText[answer.tone]
          )}
        >
          <AnswerIcon
            aria-hidden
            className={cn('size-3.5', answer.tone === 'info' && 'animate-spin')}
          />
          {answer.label}
        </span>
      </a>
    </li>
  )
}
